import React, { useState, useEffect } from 'react';
import { FileDown, Search, Filter, Package, DollarSign, Weight } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import * as XLSX from 'xlsx';
import { todayCentral } from '../../shared/utils/dateUtils';

const AvailableStock = () => {
  // State
  const [inventory, setInventory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  const [hasUncategorized, setHasUncategorized] = useState(false);
  const [goldRateUsed, setGoldRateUsed] = useState(null);
  
  // Filter states
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    vendor: 'all',
    purity: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  // Fetch filter options
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Re-fetch category dropdown whenever status filter changes
  useEffect(() => {
    fetchCategoriesForStatus(filters.status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  // Fetch report data when filters change
  useEffect(() => {
    fetchInventoryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      // Fetch vendors
      const vendorsRes = await fetch('/api/vendors', { credentials: 'include' });
      const vendorsData = await vendorsRes.json();
      if (Array.isArray(vendorsData)) {
        setVendors(vendorsData);
      } else if (vendorsData.success && Array.isArray(vendorsData.data)) {
        setVendors(vendorsData.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  // Fetch only categories that have items for the given status
  const fetchCategoriesForStatus = async (status = 'all') => {
    try {
      const params = status && status !== 'all' ? `?status=${status}` : '';
      const res = await fetch(`/api/reports/inventory/categories-in-stock${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories);
        setHasUncategorized(data.hasUncategorized);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchInventoryReport = async () => {
    try {
      setLoading(true);
      
      // Build query string
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`/api/reports/inventory?${queryParams.toString()}`, {
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success) {
        setInventory(data.data.items);
        setSummary(data.data.summary);
        setCategoryBreakdown(data.data.categoryBreakdown);
        setGoldRateUsed(data.data.goldRateUsed || null);
      } else {
        alert('Failed to load inventory report: ' + data.error);
      }
    } catch (error) {
      console.error('Error fetching inventory report:', error);
      alert('Error loading inventory report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setCurrentPage(1);
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      status: 'all',
      vendor: 'all',
      purity: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    // Reset category dropdown to full in-stock list
    fetchCategoriesForStatus('all');
  };

  const handleExportToExcel = async () => {
    try {
      // Build query string
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });

      const response = await fetch(`/api/reports/inventory/export?${queryParams.toString()}`, {
        credentials: 'include'
      });

      const data = await response.json();
      
      if (data.success && data.data.length > 0) {
        // Create workbook
        const wb = XLSX.utils.book_new();
        
        // Sheet 1 — Inventory summary (unchanged)
        const ws = XLSX.utils.json_to_sheet(data.data);
        XLSX.utils.book_append_sheet(wb, ws, 'Inventory Report');

        // Sheet 2 — Per-stone details (only if any stones exist)
        if (data.stones && data.stones.length > 0) {
          const wsStones = XLSX.utils.json_to_sheet(data.stones);
          XLSX.utils.book_append_sheet(wb, wsStones, 'Stone Details');
        }
        
        // Generate filename with date
        const date = todayCentral();
        const filename = `Inventory_Report_${date}.xlsx`;
        
        // Download file
        XLSX.writeFile(wb, filename);
        
        const stoneMsg = data.stones && data.stones.length > 0
          ? ` | ${data.stones.length} stone entries in "Stone Details" sheet`
          : '';
        alert(`Report exported successfully! (${data.data.length} items${stoneMsg})`);
      } else {
        alert('No data to export');
      }
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting report');
    }
  };

  const formatCurrency = (amount) => {
    // Apply Math.ceil to match inventory display rounding (rounds up to nearest integer)
    const roundedAmount = Math.ceil(parseFloat(amount) || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedAmount);
  };

  const formatWeight = (weight) => {
    return parseFloat(weight || 0).toFixed(3);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageIdentifier screenId={SCREEN_IDS.REPORTS.INVENTORY_REPORT} />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Inventory Report</h1>
          <p className="text-sm text-gray-600 mt-1">Available stock and inventory analysis</p>
        </div>
        <button
          onClick={handleExportToExcel}
          disabled={!inventory || inventory.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          <FileDown size={20} />
          Export to Excel
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Items</p>
                <p className="text-2xl font-bold text-gray-800">{summary.total_items}</p>
              </div>
              <Package className="text-blue-500" size={40} />
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Available: {summary.available_count} | Sold: {summary.sold_count}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Gross Weight</p>
                <p className="text-2xl font-bold text-gray-800">{formatWeight(summary.total_gross_weight)}g</p>
              </div>
              <Weight className="text-amber-600" size={40} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Net Weight</p>
                <p className="text-2xl font-bold text-gray-800">{formatWeight(summary.total_net_weight)}g</p>
              </div>
              <Weight className="text-amber-500" size={40} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Cost Value</p>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(summary.total_cost_value)}</p>
              </div>
              <DollarSign className="text-red-500" size={40} />
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sale Value</p>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(summary.total_sale_value)}</p>
                {goldRateUsed > 0 && (
                  <p className="text-xs text-amber-600 mt-0.5">
                    @ ₹{parseFloat(goldRateUsed).toLocaleString('en-IN')}/g (today's rate)
                  </p>
                )}
              </div>
              <DollarSign className="text-green-500" size={40} />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      {(() => {
        const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';
        const fieldCls = 'w-full px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
        return (
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter size={16} className="text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Filters</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Search */}
              <div>
                <label className={labelCls}>Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    placeholder="Item code, category…"
                    className={`${fieldCls} pl-8`}
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className={labelCls}>Category</label>
                <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className={fieldCls}>
                  <option value="all">All Categories</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  {hasUncategorized && <option value="uncategorized">— Uncategorized —</option>}
                </select>
              </div>

              {/* Status */}
              <div>
                <label className={labelCls}>Status</label>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className={fieldCls}>
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="sold">Sold</option>
                  <option value="return">Return</option>
                </select>
              </div>

              {/* Vendor */}
              <div>
                <label className={labelCls}>Vendor</label>
                <select value={filters.vendor} onChange={(e) => handleFilterChange('vendor', e.target.value)} className={fieldCls}>
                  <option value="all">All Vendors</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>

              {/* Purity */}
              <div>
                <label className={labelCls}>Gold Purity</label>
                <select value={filters.purity} onChange={(e) => handleFilterChange('purity', e.target.value)} className={fieldCls}>
                  <option value="all">All Purities</option>
                  <option value="18">18 KT</option>
                  <option value="22">22 KT</option>
                  <option value="24">24 KT</option>
                </select>
              </div>

              {/* Date From */}
              <div>
                <label className={labelCls}>Date From</label>
                <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} className={fieldCls} />
              </div>

              {/* Date To */}
              <div>
                <label className={labelCls}>Date To</label>
                <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} className={fieldCls} />
              </div>

              {/* Clear */}
              <div className="flex items-end">
                <button onClick={handleClearFilters} className={`${fieldCls} text-center text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors cursor-pointer`}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Category Breakdown — collapsed by default */}
      {categoryBreakdown && categoryBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-6">
          <button
            onClick={() => setShowBreakdown(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
          >
            <h2 className="text-lg font-semibold text-gray-800">Category Breakdown</h2>
            <span className="text-xs text-gray-400">{showBreakdown ? '▲ Hide' : '▼ Show'}</span>
          </button>

          {showBreakdown && (
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {categoryBreakdown.map((cat, idx) => {
                  const catId = cat.category_id ? String(cat.category_id) : 'uncategorized';
                  const isActive = filters.category === catId;
                  return (
                    <button
                      key={idx}
                      onClick={() => handleFilterChange('category', isActive ? 'all' : catId)}
                      className={`text-left border p-3 rounded-lg transition-all ${
                        isActive
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }`}
                    >
                      <p className={`text-sm font-medium ${isActive ? 'text-blue-700' : 'text-gray-700'}`}>
                        {cat.category || 'Uncategorized'}
                      </p>
                      <p className={`text-xl font-bold ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                        {cat.count} items
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatWeight(cat.total_weight)}g | {formatCurrency(cat.total_value)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Inventory Table */}
      {(() => {
        const totalPages = Math.ceil((inventory?.length || 0) / ITEMS_PER_PAGE);
        const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;
        const pageItems = inventory ? inventory.slice(pageStart, pageStart + ITEMS_PER_PAGE) : [];

        return (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading inventory report...</p>
              </div>
            ) : inventory && inventory.length > 0 ? (
              <>
                <table className="w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Item Code','Category','Purity','Gross Wt','Net Wt','Fine Net Wt','Stones','Vendor','Cost Value','Sale Value','Status','Date Added'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                      {filters.status === 'sold' && ['Sold Date','Bill No.','Customer','Sold Price'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pageItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900">{item.code}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-[140px] truncate">{item.category || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.gold_purity}KT</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatWeight(item.gross_weight)}g</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatWeight(item.net_weight)}g</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.fine_net_weight != null ? `${formatWeight(item.fine_net_weight)}g` : '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                          {item.stone_count > 0
                            ? <span>{item.stone_count} <span className="text-gray-400 text-xs">({formatWeight(item.total_stone_weight)}g)</span></span>
                            : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{item.vendor_code || '-'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatCurrency(item.calculated_cost_value || 0)}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-semibold text-green-700">{formatCurrency(item.calculated_sale_value || 0)}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-2 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                            item.status === 'In Stock' ? 'bg-green-100 text-green-800' :
                            item.status === 'Sold' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>{item.status}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-500">{new Date(item.created_at).toLocaleDateString()}</td>
                        {filters.status === 'sold' && (<>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">{item.sold_date ? new Date(item.sold_date).toLocaleDateString() : '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-medium text-blue-700">{item.sold_bill_number || '-'}</td>
                          <td className="px-3 py-2 text-gray-700 max-w-[120px] truncate">{item.sold_customer_name || '-'}</td>
                          <td className="px-3 py-2 whitespace-nowrap font-semibold text-green-700">{item.sold_price ? formatCurrency(item.sold_price) : '-'}</td>
                        </>)}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing {pageStart + 1}–{Math.min(pageStart + ITEMS_PER_PAGE, inventory.length)} of {inventory.length} items
                  </p>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >«</button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >Prev</button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                      const page = start + i;
                      return page <= totalPages ? (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1 text-xs border rounded ${
                            page === currentPage
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-100'
                          }`}
                        >{page}</button>
                      ) : null;
                    })}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >Next</button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >»</button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No items found</h3>
                <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or add new inventory items.</p>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default AvailableStock;
